<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between mb-2">
          <h2>Patient Map</h2>
          <span v-if="contacts.length || remaining">
            {{ contacts.length }} plotted<span v-if="remaining"> &mdash; geocoding {{ remaining }} more…</span>
          </span>
        </div>

        <v-alert v-if="!apiKey" type="warning">
          Google Maps API key is not configured (set VUE_APP_GOOGLE_MAPS_API_KEY).
        </v-alert>
        <v-alert v-else-if="loadError" type="error">
          Couldn't load patient addresses. Please try again later.
        </v-alert>
        <GoogleMap
          v-else
          :api-key="apiKey"
          style="width: 100%; height: 75vh"
          :center="mapCenter"
          :zoom="4"
        >
          <MarkerCluster>
            <Marker
              v-for="contact in contacts"
              :key="contact.contact_id"
              :options="{
                position: { lat: contact.latitude, lng: contact.longitude },
                title: markerTitle(contact),
              }"
            />
          </MarkerCluster>
        </GoogleMap>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { GoogleMap, Marker, MarkerCluster } from "vue3-google-map";
import ContactMapDataService from "@/services/ContactMapDataService";

// Roughly the geographic center of the contiguous US — used as a fallback map center
// until we know where the contacts actually are.
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const POLL_INTERVAL_MS = 4000;

export default {
  name: "PatientMap",
  components: { GoogleMap, Marker, MarkerCluster },
  data() {
    return {
      apiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
      contacts: [],
      remaining: 0,
      loadError: false,
      mapCenter: DEFAULT_CENTER,
      pollTimer: null,
    };
  },
  created() {
    if (this.apiKey) {
      this.fetchContacts();
    }
  },
  beforeUnmount() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },
  methods: {
    markerTitle(contact) {
      let name = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Contact";
      let place = [contact.city, contact.state].filter(Boolean).join(", ");
      return place ? `${name} — ${place}` : name;
    },
    async fetchContacts() {
      try {
        let response = await ContactMapDataService.getContacts();
        this.contacts = response.data.contacts || [];
        this.remaining = response.data.remaining || 0;
        this.loadError = false;

        if (this.mapCenter === DEFAULT_CENTER && this.contacts.length) {
          let avgLat = this.contacts.reduce((sum, c) => sum + c.latitude, 0) / this.contacts.length;
          let avgLng = this.contacts.reduce((sum, c) => sum + c.longitude, 0) / this.contacts.length;
          this.mapCenter = { lat: avgLat, lng: avgLng };
        }

        if (this.remaining > 0) {
          this.pollTimer = setTimeout(() => this.fetchContacts(), POLL_INTERVAL_MS);
        }
      } catch (err) {
        this.loadError = true;
        console.error("Failed to load patient map data", err);
      }
    },
  },
};
</script>
